import React, {useContext, useEffect, useRef, useState} from 'react';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../ui/toast';
import {
  BriefcaseBusinessIcon,
  ChartIcons,
  ClipBoardIcons,
  DashboardIcons,
  LightningIcons,
  NotebookIcons,
} from '../../../../icons/icons';
import {
  createApplicantNote,
  deleteApplicantNote,
  editJobApplicant,
} from '../../../../utils/api_service/JobOpenings';
import { Applicant } from '../../../../utils/types/JobOpenings';
import {
  AddNoteDialog,
  DocumentsTab,
  EducationTab,
  ExperienceTab,
  NotesTab,
  OverviewTab,
  PreQualResultsTab,
  SkillsTab,
} from './Components';
import { useCandidate } from './Hooks/UseCandidate';
import { PDFDisplay } from './PDFDisplay';
import { CCAResultsTab } from './Tabs/CCAResultsTab';
import {useSelector} from "react-redux";
import {RootState} from "../../../../utils/redux_store/store";
import {JobOpeningsContext} from "../../../../utils/contexts/JobOpening";

interface CandidateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidates: Applicant[];
  initialIndex: number;
  tileName?: string;
  onCandidateChange?: (candidate: Applicant, index: number) => void;
  handleSendEmailToSingleApplicant?: (
    applicant: Applicant,
    updateStatusFirst?: boolean,
  ) => Promise<void>;
}

export const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({
  isOpen,
  onClose,
  candidates,
  initialIndex,
  tileName,
  onCandidateChange,
  handleSendEmailToSingleApplicant,
}) => {
  const { id } = useParams<{ id: string }>();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const currentCandidate = candidates[currentIndex];

  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const { jobOpeningElements } = useContext(JobOpeningsContext);

  // Use the same hook as CandidateProfile for consistent behavior
  const {
    applicant,
    loading,
    isHighlightHit,
    getJobApplicants,
    selectedDocument,
    setSelectedDocument,
    pdfUrl,
    docLoading,
    handleResendInvite,
    isResending,
  } = useCandidate({
    selectedCandidate: currentCandidate,
    handleSendEmailToSingleApplicant,
  });

  // Manage activeTab locally since modal has different tabs than CandidateProfile
  const [activeTab, setActiveTab] =
    useState<
      | 'overview'
      | 'skills'
      | 'experience'
      | 'education'
      | 'prequal'
      | 'notes'
      | 'documents'
      | 'cca'
    >('overview');

  // Modal and dropdown state
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCandidateSearch, setShowCandidateSearch] = useState(false);
  const [candidateSearchTerm, setCandidateSearchTerm] = useState('');
  const [autoAdvanceToNext, setAutoAdvanceToNext] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Dropdown options for Move To functionality

  const options = [
    'New',
    'Shortlisted',
    'Suitable',
    'Outstanding',
    'Auto-Assessment',
    'Unsuccessful', // Show as 'Unsuccessful' but handle as 'Unsuccessful-Pending' in logic
    ...(jobOpeningElements?.includes('Psychometric') ? ['Psychometric'] : []),
    ...(jobOpeningElements?.includes('Wellness') ? ['Wellness'] : []),
  ];

  // Tab configuration matching CandidateProfile
  const tabs = [
    {
      id: 'overview' as const,
      label: 'Overview',
      icon: <DashboardIcons className='w-4 h-4' />,
    },
    {
      id: 'skills' as const,
      label: 'Skills',
      icon: <LightningIcons className='w-4 h-4' />,
    },
    {
      id: 'experience' as const,
      label: 'Experience',
      icon: <BriefcaseBusinessIcon className='w-4 h-4' />,
    },
    {
      id: 'education' as const,
      label: 'Education',
      icon: <NotebookIcons className='w-4 h-4' />,
    },
    {
      id: 'prequal' as const,
      label: 'Pre-Qual',
      icon: <ClipBoardIcons className='w-3 h-3' />,
    },
    {
      id: 'notes' as const,
      label: 'Notes',
      icon: <NotebookIcons className='w-4 h-4' />,
    },
    {
      id: 'documents' as const,
      label: 'Documents',
      icon: <ClipBoardIcons className='w-4 h-4' />,
    },
    {
      id: 'cca' as const,
      label: 'CCA Results',
      icon: <ChartIcons className='w-4 h-4' />,
    },
  ];

  // Compute matched skills using the same logic as CandidateProfile
  const matchedSkills = currentCandidate?.matchedSkills || [];

  // Handle saving note - same as CandidateProfile
  const handleSaveNote = async (note: any) => {
    try {
      const response = await createApplicantNote(currentCandidate.userId, note);
      if (response.ok) {
        getJobApplicants();
      }
    } catch (e) {
      console.log(e);
    }
    setIsNoteDialogOpen(false);
  };

  // Handle delete note - same as CandidateProfile
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteApplicantNote(id);
      if (response.ok) {
        getJobApplicants();
        setIsModalOpen(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  // Handle dropdown button click
  const handleButtonClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Handle dropdown option selection
  const handleOptionClick = async (option: string) => {
    const optionClicked =
        option === 'Unsuccessful' ? 'Unsuccessful-Pending' : option;

    try {
      const response = await editJobApplicant(id!, currentCandidate.userId, {
        applicantStatus: optionClicked,
      });
      if (!response.ok) {
        toast.error('Error changing applicant status', {
          position: 'bottom-center',
        });
        setIsDropdownOpen(false);
        return;
      }
      toast.success(
          `${currentCandidate.firstName} ${currentCandidate.lastName} moved to ${
              option === 'Unsuccessful' ? 'Unsuccessful (Pending)' : option
          }`,
          { position: 'bottom-center' },
      );
      // Trigger refresh of candidate data
      getJobApplicants();

      // Auto-advance to next candidate if checkbox is checked and next candidate exists
      if (autoAdvanceToNext && currentIndex < candidates.length - 1) {
        setTimeout(() => {
          goToNext();
        }, 500); // Small delay to show success message
      }
    } catch {
      toast.error('Error changing applicant status', {
        position: 'bottom-center',
      });
    }
    setIsDropdownOpen(false);
  };

  // Handle CV download with Blob approach to ensure correct filename/extension
  const handleDownloadCV = async () => {
    if (!applicant?.pdfUrl) return;

    let url = applicant.pdfUrl;
    if (!url.startsWith('http')) {
      url = `${window.location.origin}${url}`;
    }

    const toastId = toast.info('Preparing download...', { autoClose: false });

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Determine extension
      const extension = url.split('?')[0].split('.').pop() || 'pdf';

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${fullName}_Resume.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(toastId);
    } catch (error) {
      console.error('Download failed:', error);
      toast.dismiss(toastId);

      // Fallback to standard download if fetch fails (e.g. CORS)
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      const extension = url.split('?')[0].split('.').pop() || 'pdf';
      link.download = `${fullName}_Resume.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.warn(
        'Download initiated via fallback. Checking extension compatibility.',
      );
    }
  };

  // Helper function for progress dots
  const showProgressDots = candidates.length <= 20;

  // Filter candidates based on search term
  const filteredCandidates = candidateSearchTerm
    ? candidates.filter((candidate) => {
      const fullName =
          candidate.name || `${candidate.firstName} ${candidate.lastName}`;
      return (
        fullName.toLowerCase().includes(candidateSearchTerm.toLowerCase()) ||
          candidate.email
            ?.toLowerCase()
            .includes(candidateSearchTerm.toLowerCase()) ||
          candidate.applicantStatus
            ?.toLowerCase()
            .includes(candidateSearchTerm.toLowerCase())
      );
    })
    : candidates;

  // Handle candidate search
  const handleCandidateSearch = (searchTerm: string) => {
    setCandidateSearchTerm(searchTerm);
    if (searchTerm && filteredCandidates.length > 0) {
      // Jump to first matching candidate
      const firstMatchIndex = candidates.findIndex((candidate) => {
        const fullName =
          candidate.name || `${candidate.firstName} ${candidate.lastName}`;
        return (
          fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          candidate.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          candidate.applicantStatus
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      });
      if (firstMatchIndex !== -1) {
        setCurrentIndex(firstMatchIndex);
      }
    }
  };

  // Handle jump to specific candidate number
  const handleJumpToCandidate = (candidateNumber: number) => {
    const index = candidateNumber - 1;
    if (index >= 0 && index < candidates.length) {
      setCurrentIndex(index);
      setShowCandidateSearch(false);
      setCandidateSearchTerm('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Focus search input when opened
  useEffect(() => {
    if (showCandidateSearch && searchRef.current) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 100); // Small delay to ensure the panel is rendered
    }
  }, [showCandidateSearch]);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      // Reset to overview tab when candidate changes
      setActiveTab('overview');
    }
  }, [initialIndex, isOpen]);

  useEffect(() => {
    if (currentCandidate && onCandidateChange) {
      onCandidateChange(currentCandidate, currentIndex);
    }
  }, [currentIndex, currentCandidate, onCandidateChange]);

  const goToPrevious = () => {
    const newIndex =
      currentIndex > 0 ? currentIndex - 1 : candidates.length - 1;
    setCurrentIndex(newIndex);
  };

  const goToNext = () => {
    const newIndex =
      currentIndex < candidates.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(newIndex);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        goToNext();
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !currentCandidate) return null;

  const getApplicantInitials = (applicant: Applicant) => {
    const fullName =
      applicant.name || `${applicant.firstName} ${applicant.lastName}`;
    const nameParts = fullName.split(' ').filter(Boolean);
    if (nameParts.length >= 2) {
      return (
        nameParts[0][0].toUpperCase() +
        nameParts[nameParts.length - 1][0].toUpperCase()
      );
    } else if (nameParts.length === 1) {
      return nameParts[0][0].toUpperCase();
    }
    return 'U';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Outstanding':
        return 'bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 border-yellow-200';
      case 'Suitable':
        return 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border-emerald-200';
      case 'New':
        return 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200';
      case 'Shortlisted':
        return 'bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 border-purple-200';
      case 'Auto-Assessment':
        return 'bg-gradient-to-r from-cyan-100 to-blue-100 text-cyan-800 border-cyan-200';
      default:
        if (status?.includes('Unsuccessful')) {
          return 'bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border-red-200';
        }
        return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border-gray-200';
    }
  };

  const fullName =
    currentCandidate.name ||
    `${currentCandidate.firstName} ${currentCandidate.lastName}`;

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      { /* Backdrop */ }
      <div
          className='fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity'
          onClick={onClose}
      />

      { /* Modal */ }
      <div className='flex min-h-full items-center justify-center p-4'>
        <div className='relative w-full max-w-[95%] mx-auto bg-white rounded-2xl shadow-2xl transform transition-all flex h-[95vh]'>
          { /* Main Modal Content */ }
          <div
              key={currentCandidate.userId}
              className='flex-1 flex flex-col min-h-0'
          >
            { /* Header */ }
            <div className='flex-shrink-0 flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 rounded-tl-2xl'>
              <div className='flex items-center gap-4'>
                { /* Avatar */ }
                <div className='relative'>
                  { currentCandidate.symbol &&
                  currentCandidate.symbol.startsWith('image://') ? (
                    <>
                        <img
                          src={currentCandidate.symbol.replace('image://', '')}
                          alt={fullName}
                          className='w-12 h-12 rounded-xl object-cover shadow-md'
                          onError={(e) => {
                              e.currentTarget.style.display = 'none';
                              const fb = e.currentTarget.nextElementSibling as HTMLElement;
                              if (fb) fb.style.display = 'flex';
                            }}
                        />
                        <div
                          className='w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-xl items-center justify-center text-white font-bold text-base shadow-md'
                          style={{ display: 'none' }}
                        >
                        { getApplicantInitials(currentCandidate) }
                      </div>
                      </>
                    ) : (
                      <div className='w-12 h-12 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-base shadow-md'>
                        { getApplicantInitials(currentCandidate) }
                      </div>
                    ) }
                </div>

                { /* Name and Status */ }
                <div className='flex-grow'>
                  <div className='flex flex-col'>
                    <h2 className='text-lg font-bold text-gray-900'>
                      { fullName }
                    </h2>
                    { tileName && (
                      <p className='text-sm text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-md mt-1 inline-block'>
                        { tileName } Category
                      </p>
                    ) }
                  </div>
                  <div className='flex items-center gap-3 mt-2'>
                    <div
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(
                          applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                          applicant?.applicantStatus === 'Unsuccessful-Pending'
                            ? 'Unsuccessful'
                            : applicant?.applicantStatus ||
                              currentCandidate.applicantStatus ||
                              '',
                        )}`}
                    >
                      { applicant?.applicantStatus === 'Unsuccessful-Closed' ||
                      applicant?.applicantStatus === 'Unsuccessful-Pending'
                        ? 'Unsuccessful'
                        : applicant?.applicantStatus ||
                          currentCandidate.applicantStatus }
                    </div>
                    <span className='text-sm text-gray-600'>
                      { currentIndex + 1 } of { candidates.length }{ ' ' }
                      { tileName ? `in ${tileName}` : '' }
                    </span>
                  </div>
                </div>

                { /* Action Buttons */ }
                <div className='flex gap-2'>
                  { /* Add Note Button */ }
                  <button
                      onClick={() => setIsNoteDialogOpen(true)}
                      className='group bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-3 py-2 rounded-lg text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2'
                      aria-label='Add note about candidate'
                  >
                    <svg
                        className='w-4 h-4'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                      />
                    </svg>
                    <span>Add Note</span>
                  </button>

                  { /* Move To Dropdown */ }
                  <div className='relative' ref={dropdownRef}>
                    <button
                        onClick={handleButtonClick}
                        className='group bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2'
                        aria-label='Move candidate to different status'
                        aria-expanded={isDropdownOpen}
                    >
                      <span>Move to</span>
                      <svg
                          className={`w-4 h-4 transition-transform duration-200 ${
                            isDropdownOpen ? 'rotate-180' : ''
                          }`}
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 9l-7 7-7-7'
                        />
                      </svg>
                    </button>

                    { isDropdownOpen && (
                      <div className='absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl w-52 z-30 overflow-hidden animate-slideDown'>
                        { options.map((option, idx) => (
                          <button
                              key={idx}
                              onClick={() => handleOptionClick(option)}
                              className='w-full text-left px-4 py-2.5 text-sm cursor-pointer text-gray-700 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 hover:text-green-700 transition-all duration-150 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-green-50'
                          >
                            { option }
                          </button>
                        )) }
                      </div>
                    ) }
                  </div>

                  { /* Auto-advance checkbox */ }
                  <div className='flex items-center gap-2 ml-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200'>
                    <input
                        type='checkbox'
                        id='autoAdvance'
                        checked={autoAdvanceToNext}
                        onChange={(e) => setAutoAdvanceToNext(e.target.checked)}
                        className='w-4 h-4 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 focus:ring-2 cursor-pointer'
                    />
                    <label
                        htmlFor='autoAdvance'
                        className='text-sm font-medium text-gray-700 cursor-pointer select-none'
                        title='Automatically move to next candidate after status change'
                    >
                      Auto-advance
                    </label>
                  </div>
                </div>
              </div>

              { /* Navigation Controls */ }
              <div className='flex items-center gap-2 mt-4'>
                { /* Global Candidate Search - Always Available */ }
                <button
                    onClick={() => setShowCandidateSearch(!showCandidateSearch)}
                    className={`h-10 px-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-center group text-sm font-medium ${
                      showCandidateSearch
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 border-emerald-400 text-white shadow-lg shadow-emerald-500/25'
                        : 'bg-white border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 text-slate-600 hover:text-emerald-600'
                    }`}
                    title='Search all candidates'
                >
                  <svg
                      className={`w-4 h-4 mr-2 transition-transform duration-200 ${
                        showCandidateSearch ? 'rotate-90' : ''
                      }`}
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                    />
                  </svg>
                  <span>Search</span>
                  { candidates.length > 1 && (
                    <span
                        className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                          showCandidateSearch
                            ? 'bg-white/20 text-white'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                    >
                      { candidates.length }
                    </span>
                  ) }
                </button>

                <button
                    onClick={goToPrevious}
                    className='h-10 w-10 rounded-xl bg-white border-2 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 flex items-center justify-center transition-all duration-200 group'
                    title='Previous candidate (←)'
                >
                  <svg
                      className='w-5 h-5 text-slate-600 group-hover:text-emerald-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 19l-7-7 7-7'
                    />
                  </svg>
                </button>

                <button
                    onClick={goToNext}
                    className='h-10 w-10 rounded-xl bg-white border-2 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 flex items-center justify-center transition-all duration-200 group'
                    title='Next candidate (→)'
                >
                  <svg
                      className='w-5 h-5 text-slate-600 group-hover:text-emerald-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M9 5l7 7-7 7'
                    />
                  </svg>
                </button>

                <button
                    onClick={onClose}
                    className='h-10 w-10 rounded-xl bg-white border-2 border-slate-200 hover:border-red-300 hover:bg-red-50 flex items-center justify-center transition-all duration-200 group ml-2'
                    title='Close (Esc)'
                >
                  <svg
                      className='w-5 h-5 text-slate-600 group-hover:text-red-600'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                  >
                    <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>

            { /* Tabs Navigation */ }
            <div className='flex-shrink-0 border-b border-gray-200 bg-white'>
              <nav className='flex space-x-8 px-6' aria-label='Tabs'>
                { tabs.map((tab) => (
                  <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-[10px] transition-all duration-200 flex items-center gap-1 ${
                        activeTab === tab.id
                          ? 'border-green-600 text-green-600 bg-green-50/50'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    <span className='scale-50 origin-center'>{ tab.icon }</span>
                    { tab.label }
                  </button>
                )) }
              </nav>
            </div>

            { /* Content */ }
            <div className='flex-1 flex overflow-hidden min-h-0'>
              { /* Left Side - Tabbed Content */ }
              <div className='flex-1 flex flex-col min-w-0'>
                <div className='flex-1 overflow-y-auto custom-scrollbar'>
                  { /* Overview Tab */ }
                  { activeTab === 'overview' && (
                    <div className='p-6'>
                      <OverviewTab
                          address={applicant?.contactInfo?.address}
                          applicantStatus={
                          // Normalize status display - show both Unsuccessful-Closed and Unsuccessful-Pending as 'Unsuccessful'
                            applicant?.applicantStatus ===
                            'Unsuccessful-Closed' ||
                          applicant?.applicantStatus === 'Unsuccessful-Pending'
                              ? 'Unsuccessful'
                              : applicant?.applicantStatus ??
                              applicant?.status ??
                              'N/A'
                          }
                          email={applicant?.email ?? 'N/A'}
                          phone={applicant?.contactInfo?.phone ?? 'N/A'}
                          socials={applicant?.socials}
                          notes={(applicant as any)?.notes}
                          aiScore={(applicant as any)?.aiScore}
                          statusHistory={applicant?.statusHistory || (currentCandidate as any)?.statusHistory}
                          createdAt={applicant?.createdAt || (currentCandidate as any)?.createdAt}
                      />
                    </div>
                  ) }

                  { /* Skills Tab */ }
                  { activeTab === 'skills' && (
                    <div className='p-6'>
                      <SkillsTab
                          skills={applicant?.skills}
                          isHighlightHit={isHighlightHit}
                          matchedSkills={matchedSkills}
                          behavioralAttributes={applicant?.behavioralAttributes}
                          matchedBehavior={currentCandidate.matchedBehavior}
                      />
                    </div>
                  ) }

                  { /* Experience Tab */ }
                  { activeTab === 'experience' && (
                    <div className='p-6'>
                      <ExperienceTab experience={applicant?.workExperience} />
                    </div>
                  ) }

                  { /* Education Tab */ }
                  { activeTab === 'education' && (
                    <div className='p-6'>
                      <EducationTab educations={applicant?.educations} />
                    </div>
                  ) }

                  { /* Pre-Qualifying Results Tab */ }
                  { activeTab === 'prequal' && (
                    <div className='p-6'>
                      <PreQualResultsTab
                          jobOpeningId={id!}
                          jobApplicantId={currentCandidate.userId}
                          handleResendInvite={handleResendInvite}
                          isResending={isResending}
                      />
                    </div>
                  ) }

                  { /* Notes Tab */ }
                  { activeTab === 'notes' && (
                    <div className='p-6'>
                      <NotesTab
                          notes={(applicant as any)?.conversationNotes}
                          getJobApplicants={getJobApplicants}
                          onDelete={handleDelete}
                          isModalOpen={isModalOpen}
                          setIsModalOpen={setIsModalOpen}
                      />
                    </div>
                  ) }

                  { /* Documents Tab */ }
                  { activeTab === 'documents' && (
                    <div className='p-6'>
                      <DocumentsTab
                          documents={applicant?.applicantDocuments || []}
                          selectedDocumentId={selectedDocument?.id}
                          onDocumentSelect={setSelectedDocument}
                          pdfUrl={applicant?.pdfUrl}
                          baseFilename={applicant?.baseFilename}
                          uploadedAt={applicant?.createdAt}
                      />
                    </div>
                  ) }

                  { /* CCA Results Tab */ }
                  { activeTab === 'cca' && (
                    <div className='p-6'>
                      <CCAResultsTab candidate={currentCandidate} />
                    </div>
                  ) }
                </div>
              </div>

              { /* Right Side - CV/Resume (Always Visible) */ }
              <div className='flex-1 flex flex-col border-l border-gray-200 min-w-0'>
                { /* CV Header with Controls */ }
                <div className='flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50'>
                  <div className='flex items-center gap-3'>
                    <svg
                        className='w-6 h-6 text-blue-600'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                      />
                    </svg>
                    <div>
                      <h3 className='font-semibold text-gray-900'>
                        { selectedDocument
                          ? selectedDocument.requirementLabel
                          : 'CV/Resume' }
                      </h3>
                      <p className='text-sm text-gray-600'>
                        { selectedDocument
                          ? selectedDocument.baseFilename
                          : fullName }
                      </p>
                    </div>
                  </div>

                  <div className='flex items-center gap-2'>
                    { selectedDocument &&
                      selectedDocument.requirementKey !== 'resume' && (
                      <button
                          onClick={() => {
                            const resume = applicant?.applicantDocuments?.find(
                              (doc) => doc.requirementKey === 'resume',
                            );
                            if (resume) setSelectedDocument(resume);
                          }}
                          className='px-3 py-1.5 text-xs font-bold text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-200 flex items-center gap-1.5'
                      >
                        <DashboardIcons className='w-3 h-3' />
                        Show Main CV
                      </button>
                    ) }
                    { /* Download Button */ }
                    <button
                        onClick={handleDownloadCV}
                        className='group bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2'
                        title='Download CV/Resume'
                    >
                      <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                        />
                      </svg>
                      <span>Download</span>
                    </button>
                  </div>
                </div>

                <div className='flex-1 relative bg-white'>
                  { loading ? (
                    <div className='flex items-center justify-center h-full'>
                      <div className='text-center'>
                        <div className='relative'>
                          <div className='animate-spin rounded-full h-16 w-16 border-4 border-gray-300 border-t-green-600 mx-auto mb-4'></div>
                          <div className='absolute inset-0 flex items-center justify-center'>
                            <div className='w-8 h-8 bg-green-600 rounded-full opacity-20 animate-pulse'></div>
                          </div>
                        </div>
                        <p className='text-lg text-gray-700 font-semibold'>
                          Loading Document...
                        </p>
                        <p className='text-sm text-gray-500 mt-2'>
                          Please wait while we fetch the document
                        </p>
                      </div>
                    </div>
                  ) : pdfUrl || docLoading ? (
                    <div className='h-full'>
                      <PDFDisplay pdfUrl={pdfUrl} />
                    </div>
                  ) : (
                    <div className='flex items-center justify-center h-full'>
                      <div className='text-center p-8'>
                        <svg
                            className='w-20 h-20 text-gray-400 mx-auto mb-4'
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                        >
                          <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                          />
                        </svg>
                        <p className='text-lg text-gray-600 font-medium'>
                          No Document Available
                        </p>
                        <p className='text-sm text-gray-500 mt-2'>
                          Document not uploaded for this candidate
                        </p>
                      </div>
                    </div>
                  ) }
                </div>
              </div>
            </div>

            { /* Custom scrollbar styles and animations */ }
            <style>{ `
            .custom-scrollbar::-webkit-scrollbar {
              width: 6px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #10b981;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #059669;
            }
            .animate-slideDown {
              animation: slideDown 0.2s ease-out;
            }
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-8px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            .animate-slideUp {
              animation: slideUp 0.2s ease-out;
            }
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(8px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          ` }</style>

            { /* Footer */ }
            <div className='flex-shrink-0 flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gradient-to-r from-slate-50 via-white to-slate-50 rounded-bl-2xl'>
              <div className='text-sm text-gray-600'>
                <span>
                  Use ← → arrow keys to navigate, Search to find candidates, Esc
                  to close
                </span>
              </div>
              <div className='flex items-center gap-4'>
                { /* Smart Progress Indicators */ }
                { showProgressDots ? (
                  <div className='flex items-center gap-2'>
                    { candidates.map((_, index) => (
                      <button
                          key={index}
                          onClick={() => setCurrentIndex(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-200 ${
                            index === currentIndex
                              ? 'bg-emerald-500 w-4'
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                          title={`Go to candidate ${index + 1}`}
                      />
                    )) }
                  </div>
                ) : (
                  <div className='flex items-center gap-3'>
                    { /* Page-based navigation for large datasets */ }
                    <div className='text-sm text-gray-500'>
                      Page { Math.floor(currentIndex / 10) + 1 } of{ ' ' }
                      { Math.ceil(candidates.length / 10) }
                    </div>
                    <div className='flex gap-1'>
                      { /* Show page indicators instead of individual dots */ }
                      { Array.from(
                        {
                          length: Math.min(
                            10,
                            Math.ceil(candidates.length / 10),
                          ),
                        },
                        (_, pageIndex) => {
                          const isCurrentPage =
                            Math.floor(currentIndex / 10) === pageIndex;
                          return (
                            <button
                                key={pageIndex}
                                onClick={() => setCurrentIndex(pageIndex * 10)}
                                className={`w-8 h-6 rounded text-xs font-medium transition-all duration-200 ${
                                  isCurrentPage
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                }`}
                                title={`Go to page ${pageIndex + 1}`}
                            >
                              { pageIndex + 1 }
                            </button>
                          );
                        },
                      ) }
                    </div>
                  </div>
                ) }

                { /* Candidate counter */ }
                <div className='text-sm font-medium text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200'>
                  { currentIndex + 1 } / { candidates.length }{ ' ' }
                  { tileName ? `in ${tileName}` : '' }
                </div>
              </div>
            </div>
          </div>

          { /* Search Panel - Slide out from the right */ }
          { showCandidateSearch && (
            <div className='w-96 bg-white/95 backdrop-blur-sm border-l border-gray-100 shadow-xl flex flex-col rounded-r-2xl max-h-[90vh]'>
              <div className='p-6 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-tr-2xl flex-shrink-0'>
                <div className='flex justify-between items-center'>
                  <h3 className='text-lg font-semibold text-gray-900 flex items-center gap-2'>
                    <svg
                        className='w-5 h-5 text-emerald-600'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                      />
                    </svg>
                    Find Candidate
                  </h3>
                  <button
                      onClick={() => setShowCandidateSearch(false)}
                      className='p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/50 transition-all duration-200'
                  >
                    <svg
                        className='w-5 h-5'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M6 18L18 6M6 6l12 12'
                      />
                    </svg>
                  </button>
                </div>
                <p className='text-sm text-gray-600 mt-2 font-medium'>
                  { candidates.length } candidate
                  { candidates.length !== 1 ? 's' : '' } total
                </p>
              </div>

              <div className='flex-1 overflow-y-auto p-6 space-y-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent min-h-0'>
                { /* Search Input */ }
                <div>
                  <label className='text-sm font-medium text-gray-700 mb-2 block'>
                    Search by name, email, or status
                  </label>
                  <div className='relative'>
                    <input
                        ref={searchRef}
                        type='text'
                        value={candidateSearchTerm}
                        onChange={(e) => handleCandidateSearch(e.target.value)}
                        placeholder='Start typing to search...'
                        className='w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50 hover:bg-white transition-colors duration-200'
                    />
                    <svg
                        className='absolute left-3 top-3.5 w-4 h-4 text-gray-400'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                      <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                      />
                    </svg>
                  </div>
                </div>

                { /* Direct Jump */ }
                <div>
                  <label className='text-sm font-medium text-gray-700 mb-2 block'>
                    Jump to candidate number
                  </label>
                  <div className='flex gap-2'>
                    <input
                        type='number'
                        min='1'
                        max={candidates.length}
                        placeholder={`1-${candidates.length}`}
                        className='flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50 hover:bg-white transition-colors duration-200'
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const num = parseInt(
                              (e.target as HTMLInputElement).value,
                            );
                            if (num) handleJumpToCandidate(num);
                          }
                        }}
                    />
                    <button
                        onClick={() => {
                          const input = document.querySelector(
                            'input[type="number"]',
                          ) as HTMLInputElement;
                          const num = parseInt(input?.value || '0');
                          if (num) handleJumpToCandidate(num);
                        }}
                        className='px-4 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:shadow-lg hover:shadow-emerald-500/25 transition-all duration-200 text-sm font-medium'
                    >
                      Go
                    </button>
                  </div>
                </div>

                { /* Search Results */ }
                { candidateSearchTerm ? (
                  <div className='border-t pt-4'>
                    <div className='text-sm text-gray-600 mb-3 flex items-center gap-2'>
                      <svg
                          className='w-4 h-4 text-emerald-500'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                        />
                      </svg>
                      Found{ ' ' }
                      <span className='font-semibold text-emerald-600'>
                        { filteredCandidates.length }
                      </span>{ ' ' }
                      matching candidates
                    </div>
                    <div className='space-y-2'>
                      { filteredCandidates.map((candidate) => {
                        const candidateIndex = candidates.findIndex(
                          (c) => c.userId === candidate.userId,
                        );
                        const fullName =
                          candidate.name ||
                          `${candidate.firstName} ${candidate.lastName}`;
                        const initials = getApplicantInitials(candidate);

                        return (
                          <div
                              key={candidate.userId}
                              onClick={() => {
                                setCurrentIndex(candidateIndex);
                                setShowCandidateSearch(false);
                                setCandidateSearchTerm('');
                              }}
                              className='p-4 border border-gray-200 rounded-lg hover:bg-gradient-to-r hover:from-emerald-50 hover:to-blue-50 cursor-pointer transition-all duration-300 hover:shadow-sm'
                          >
                            <div className='flex items-center space-x-3'>
                              <div className='flex-shrink-0'>
                                { candidate.symbol &&
                                candidate.symbol.startsWith('image://') ? (
                                  <>
                                      <img
                                        src={candidate.symbol.replace(
                                            'image://',
                                            '',
                                          )}
                                        alt={fullName}
                                        className='w-10 h-10 rounded-full object-cover'
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            const fb = e.currentTarget.nextElementSibling as HTMLElement;
                                            if (fb) fb.style.display = 'flex';
                                          }}
                                      />
                                      <div
                                        className='w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full items-center justify-center text-white font-semibold text-sm shadow-md'
                                        style={{ display: 'none' }}
                                      >
                                      { initials }
                                    </div>
                                    </>
                                  ) : (
                                    <div className='w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md'>
                                      { initials }
                                    </div>
                                  ) }
                              </div>

                              <div className='flex-1 min-w-0'>
                                <p className='text-sm font-medium text-gray-900 truncate'>
                                  { fullName }
                                </p>
                                { candidate.email && (
                                  <p className='text-sm text-gray-500 truncate'>
                                    { candidate.email }
                                  </p>
                                ) }
                                <div className='flex items-center mt-1'>
                                  <span
                                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getStatusColor(
                                        candidate.applicantStatus || '',
                                      )}`}
                                  >
                                    { candidate.applicantStatus }
                                  </span>
                                </div>
                              </div>

                              <div className='flex-shrink-0'>
                                <svg
                                    className='w-5 h-5 text-emerald-400 group-hover:text-emerald-600 transition-colors duration-200'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                  <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M9 5l7 7-7 7'
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        );
                      }) }
                    </div>
                  </div>
                ) : (
                  <div className='border-t pt-4'>
                    <div className='text-sm text-gray-600 mb-3'>
                      All Candidates
                    </div>
                    <div className='space-y-2'>
                      { candidates.map((candidate, index) => {
                        const fullName =
                          candidate.name ||
                          `${candidate.firstName} ${candidate.lastName}`;
                        const initials = getApplicantInitials(candidate);
                        const isCurrentCandidate = index === currentIndex;

                        return (
                          <div
                              key={candidate.userId}
                              onClick={() => {
                                setCurrentIndex(index);
                                setShowCandidateSearch(false);
                              }}
                              className={`p-4 border rounded-lg cursor-pointer transition-all duration-300 hover:shadow-sm ${
                                isCurrentCandidate
                                  ? 'border-emerald-300 bg-gradient-to-r from-emerald-50 to-blue-50'
                                  : 'border-gray-200 hover:bg-gradient-to-r hover:from-emerald-50 hover:to-blue-50'
                              }`}
                          >
                            <div className='flex items-center space-x-3'>
                              <div className='flex-shrink-0'>
                                { candidate.symbol &&
                                candidate.symbol.startsWith('image://') ? (
                                  <>
                                      <img
                                        src={candidate.symbol.replace(
                                            'image://',
                                            '',
                                          )}
                                        alt={fullName}
                                        className='w-10 h-10 rounded-full object-cover'
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            const fb = e.currentTarget.nextElementSibling as HTMLElement;
                                            if (fb) fb.style.display = 'flex';
                                          }}
                                      />
                                      <div
                                        className='w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full items-center justify-center text-white font-semibold text-sm shadow-md'
                                        style={{ display: 'none' }}
                                      >
                                      { initials }
                                    </div>
                                    </>
                                  ) : (
                                    <div className='w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md'>
                                      { initials }
                                    </div>
                                  ) }
                              </div>

                              <div className='flex-1 min-w-0'>
                                <div className='flex items-center gap-2'>
                                  <p className='text-sm font-medium text-gray-900 truncate'>
                                    { fullName }
                                  </p>
                                  <span className='text-xs text-emerald-600 font-medium bg-emerald-100 px-2 py-0.5 rounded-full'>
                                    #{ index + 1 }
                                  </span>
                                </div>
                                { candidate.email && (
                                  <p className='text-sm text-gray-500 truncate'>
                                    { candidate.email }
                                  </p>
                                ) }
                                <div className='flex items-center mt-1'>
                                  <span
                                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getStatusColor(
                                        candidate.applicantStatus || '',
                                      )}`}
                                  >
                                    { candidate.applicantStatus }
                                  </span>
                                </div>
                              </div>

                              <div className='flex-shrink-0'>
                                { isCurrentCandidate && (
                                  <div className='w-3 h-3 bg-emerald-500 rounded-full animate-pulse'></div>
                                ) }
                              </div>
                            </div>
                          </div>
                        );
                      }) }
                    </div>
                  </div>
                ) }
              </div>
            </div>
          ) }
        </div>
      </div>

      { /* Add Note Dialog */ }
      <AddNoteDialog
          isOpen={isNoteDialogOpen}
          onClose={() => setIsNoteDialogOpen(false)}
          onSave={handleSaveNote}
          candidateName={currentCandidate.name}
      />
    </div>
  );
};
